/* eslint-disable max-lines */
import React, { useEffect, useMemo, useState } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import styled from 'styled-components'

import ArrowIcon from '@mui/icons-material/TrendingFlat'
import { Button, type ButtonStyles } from '@nexiahome/ui-common/components/primitives/buttons'
import FeaturesBox from 'pages/paywall/FeaturesBox'
import PlanSelection from 'pages/paywall/PlanSelection'
import Spinner from 'components/Spinner'

import Brand from 'helpers/brand'
import GA4 from 'helpers/ga4'

import { useFeaturesContext } from 'hooks/FeaturesContext'
import usePaywallPlans from 'hooks/usePaywallPlans'
import useActivePremiumAccount from 'hooks/useActivePremiumAccount'
import usePaywallPremiumAcct from 'hooks/usePaywallPremiumAcct'
import useScreenSize from 'hooks/useScreenSize'
import { useAlertDialog } from 'hooks/useDialog'

import { PageTitle, screenSize } from 'styles/shared'
import { color } from 'styles/colors'

const BUTTON_TEXT_NO_FREE_TRIAL = 'Upgrade'
const BUTTON_TEXT_FREE_TRIAL = 'Start Free Trial'

const UNLOCK_TITLE = 'Unlock Next-Level Diagnostics with Premium Access'
const SMALL_SCREEN_UNLOCK_TITLE = 'Unlock Next-Level Diagnostics'
const PREMIUM_EXPLANATION = 'Upgrade to Diagnostics Premium and harness cutting-edge features designed to give you more diagnostics data, improve technician service efficiency, and enhance homeowner communication – all-in-one seamless platform.'
const WHY_TITLE = 'Why upgrade?'
const COMING_SOON_TITLE = 'More features coming soon!'
const PRICING_TITLE = 'Exclusive pricing'
const PRICING = [
  <span key={1}><b>USD $250/month</b> – flexible, cancel anytime</span>,
  <span key={2}><b>USD $2,400/ year</b> – save USD $600 with an annual subscription</span>
]
const TAXES_NOTE = 'Taxes applied at checkout'

const PaywallOptions = () => {
  const { dealerId } = useParams()
  const navigate = useNavigate()
  const dialog = useAlertDialog()

  const { enabledFeatures } = useFeaturesContext()

  const { plans, yearlyPlanNo, monthlyPlanNo, arePaywallPlansLoaded } = usePaywallPlans(dealerId)
  const { activeAccount, isActiveAccountLoaded } = useActivePremiumAccount(dealerId)
  const { premiumAccount, isPremiumAcctLoaded } = usePaywallPremiumAcct(dealerId)
  const [selectedInput, setSelectedInput] = useState(undefined as number | undefined)
  const [currentPlan, setCurrentPlan] = useState(yearlyPlanNo)
  const [planType, setPlanType] = useState<string>('diag_yearly')
  const [isNewAccount, setIsNewAccount] = useState<boolean>(true)

  const screenSize = useScreenSize()

  const WHY_UPGRADE = [
    'Extended Runtime History - go beyond 30 days, to track performance month-over-month and year-over-year for deeper insights and trend analysis.',
    `${Brand.brandName()} Tech Assistant - get AI-powered guided service assistance in the field on Technician App, helping you troubleshoot faster and smarter.`,
    'Homeowner Reports - generate co-branded, ready-to-share reports that build trust and transparency with homeowners.',
    'Historical System Report Storage - A dedicated reports section with all historical information stored in one place.',
    'Maintenance Trends - analyze system behavior across indoor, outdoor and operational metrics for more accurate diagnostics and proactive servicing.'
  ]
  const COMING_SOON = `We're continuously adding new features to expand your diagnostics capabilities. Future updates will be included at no extra costs –– ensuring immediate access to the latest advancements ${Brand.brandName()} has to offer.`

  const buttonText = useMemo(() => {
    if (enabledFeatures?.includes('paywall_trial') && isNewAccount) {
      return BUTTON_TEXT_FREE_TRIAL
    } else {
      return BUTTON_TEXT_NO_FREE_TRIAL
    }
  }, [isNewAccount, enabledFeatures])

  useEffect(() => {
    if (arePaywallPlansLoaded && isActiveAccountLoaded && isPremiumAcctLoaded) {
      if (activeAccount) {
        for (let i = 0; i < plans.length; i++) {
          if (plans[i].no.toString() === activeAccount.planNo) {
            setPlanType(plans[i].planId)
            break
          }
        }
      }

      if (planType === 'diag_yearly') {
        setCurrentPlan(yearlyPlanNo)
      } else {
        setCurrentPlan(monthlyPlanNo)
      }

      if (premiumAccount && !activeAccount) {
        setIsNewAccount(false)
      }
    }
  }, [activeAccount, arePaywallPlansLoaded, plans, isActiveAccountLoaded, monthlyPlanNo, yearlyPlanNo, premiumAccount]) // eslint-disable-line react-hooks/exhaustive-deps

  if (!arePaywallPlansLoaded || !isActiveAccountLoaded) {
    return (
      <S.EmptyPage>
        <Spinner variant="page" />
      </S.EmptyPage>
    )
  }

  const onChangeSelectedInput = (value: number) => {
    setSelectedInput(Number(value))

    if (Number(value) === PlanSelection.YEARLY) {
      setCurrentPlan(yearlyPlanNo)
      setPlanType('diag_yearly')
    } else {
      setCurrentPlan(monthlyPlanNo)
      setPlanType('diag_monthly')
    }
  }

  const onClickUpgrade = () => {
    if (selectedInput === undefined) {
      dialog.alert({
        title: 'Select a Plan',
        description: 'An Annual or Monthly plan must be selected before proceeding.',
        okText: 'OK'
      })
    } else {
      GA4.sendEvent({ category: 'Dealer Paywall', label: 'Dealer Paywall Updated', action: 'Click Upgrade' })

      navigate(`/paywall/account/${dealerId}/${currentPlan}/${planType}`)
    }
  }

  return (
    <>
      <S.Top>
        <Button decoration="link" onPress={() => navigate(-1)} aria-label="back-button">
          <S.ArrowIcon fontSize="large" />
        </Button>
        <S.ProductLogo src={Brand.premiumProductLogoPath()} alt={`${Brand.brandName()} logo`} />
        <div />
      </S.Top>
      <S.Background>
        <S.Content>
          <S.LeftSide>
            <S.FormSection>
              <S.SectionHeader>{screenSize === 'small' ? SMALL_SCREEN_UNLOCK_TITLE : UNLOCK_TITLE}</S.SectionHeader>
              <S.Explanation>{PREMIUM_EXPLANATION}</S.Explanation>
            </S.FormSection>
            <S.FormSection>
              <S.SectionHeader>{WHY_TITLE}</S.SectionHeader>
              {WHY_UPGRADE.map((reason) => (
                <S.Explanation key={reason}>
                  <S.Image src={'/img/checkmark.png'} width={40} />
                  <span>
                    <b>
                      {reason.split(' -')[0]}
                    </b>
                    <span> -</span>
                    {reason.split(' -')[1]}
                  </span>
                </S.Explanation>
              ))}
            </S.FormSection>
            <S.FormSection>
              <S.SectionHeader>{COMING_SOON_TITLE}</S.SectionHeader>
              <S.Explanation>{COMING_SOON}</S.Explanation>
            </S.FormSection>
            <S.FormSection>
              <S.SectionHeader>{PRICING_TITLE}</S.SectionHeader>
              {PRICING.map((price) => (
                <S.Explanation key={price.props.children}>
                  <S.PriceArrowIcon fontSize="large" />
                  {price}
                </S.Explanation>
              ))}
            </S.FormSection>
            <S.Note>{TAXES_NOTE}</S.Note>
          </S.LeftSide>
          <S.RightSide>
            <S.FormSection>
              <S.RightSideHeader>
                <span>Experience the difference</span>
                {isNewAccount && enabledFeatures?.includes('paywall_trial') && (
                  <>
                    <span> with a </span>
                    {screenSize === 'small' && <br />}
                    <S.RedText>30 day free trial</S.RedText>
                  </>
                )}
              </S.RightSideHeader>
              <S.Features>
                <FeaturesBox isStandardList onChangeSelection={onChangeSelectedInput} selectedInput={selectedInput} />
                <FeaturesBox isStandardList={false} onChangeSelection={onChangeSelectedInput} selectedInput={selectedInput} />
              </S.Features>
            </S.FormSection>

            {isNewAccount && enabledFeatures?.includes('paywall_trial') && (
              <S.TextUnderBoxes>
                <span>Select the Annual or Monthly plan and</span>
                <span> start your <S.RedText>30 day free trial</S.RedText> today.</span>
                <S.BlueLineForTextUnderBoxes />
              </S.TextUnderBoxes>
            )}

            {!isNewAccount && (
              <S.TextUnderBoxes>
                Select the Annual or Monthly plan to start your subscription.
              </S.TextUnderBoxes>
            )}
          </S.RightSide>
        </S.Content>

        <S.ButtonContainer>
          <Button
            decoration="positive"
            text={buttonText}
            onPress={onClickUpgrade}
            styles={UpgradeButtonStyle}
          />
        </S.ButtonContainer>
      </S.Background>
    </>
  )
}

export default PaywallOptions

const UpgradeButtonStyle: ButtonStyles = {
  button: { height: 46, borderRadius: 10, width: 200 },
  text: { textTransform: 'capitalize', fontWeight: 'bold' }
}

const blueUnderlineGradient = `
  background-image: linear-gradient(179deg, ${color('hehmModalSuccessText')} 0%, ${color('hehmModalSuccessText')} 50%,transparent 54%, transparent 100%);
  background-size:  160% 15%;
  background-repeat: repeat-x;
  background-position: left 0% bottom 10%;
`

const S = {
  EmptyPage: styled.div`
    display: flex;
    justify-content: center;
    align-items: center;
    height: 100vh;
    width: 100vw;
  `,
  Background: styled.div`
    height: 100%;
    padding: 0 20px 20px;
    display: flex;
    flex-direction: column;
    justify-content: center;
    align-items: center;
    background-image: url(${Brand.premiumBackgroundPath()});
    background-size: 100% 100%;
    background-repeat: no-repeat;
    background-attachment: fixed;

    @media ${screenSize.medium} {
      justify-content: flex-start;
      padding: 0 10px 0;
    }

    @media ${screenSize.small} {
      margin-bottom: -60px;
      background-size: 125% 100%;
      justify-content: flex-start;
    }
  `,
  ArrowIcon: styled(ArrowIcon)`
    transform: rotate(180deg);
    color: ${color('breadCrumbsTextHover')};
  `,
  ButtonContainer: styled.div`
    display: flex;
    flex-direction: column;
    margin-top: 10px;

    @media ${screenSize.small} {
      margin-top: 0;
    }
    @media ${screenSize.medium} {
      margin-top: 25px;
    }
  `,
  PriceArrowIcon: styled(ArrowIcon)`
    color: ${color('hehmModalSuccessText')};
    margin: -5px 8px;
  `,
  Container: styled.div`
    padding: 20px;
  `,
  Features: styled.div`
    display: grid;
    grid-template-columns: 1fr 1fr;
    column-gap: 10px;
  
    @media ${screenSize.small} {
      display: flex;
      flex-direction: column;
      justify-content: space-between;
      align-items: center;
      gap: 20px
    }
  `,
  Content: styled.form`
    display: flex;
    justify-content: space-between;
    width: 100%;
    max-width: 1200px;

    @media ${screenSize.medium} {
      flex-direction: column;
      align-items: center;
    }
  `,
  FormSection: styled.div`
    & > div {
      margin: 10px 0 0 0;

      & label {
        font-weight: bold;
      }
    }
  `,
  RedText: styled.span`
    color: ${color('premiumFreeTrialText')};
  `,
  RightSideHeader: styled.h3`
    margin-top: 25px;
    margin-bottom: 40px;
    text-align: center;
    text-transform: none;
    font-size: 18px;
    font-style: italic;

    @media ${screenSize.small} {
      line-height: 1.2;
    }
  `,
  SectionHeader: styled.h2`
    ${blueUnderlineGradient}

    margin-top: 25px;
    padding-bottom: 5px;
    text-transform: unset;
    width: fit-content;
  `,
  LeftSide: styled.div`
    margin-top: 70px;
    max-width: 550px;
    word-wrap: break-word;

    @media ${screenSize.medium} {
      margin-top: 0;
    }
  `,
  RightSide: styled.div`
    max-width: 720px;

    @media ${screenSize.medium} {
      margin-top: 10px;
    }
  `,
  Explanation: styled.div`
    display: flex;
    margin: 0 auto 20px;
    text-align: left;
  `,
  PageTitle: styled(PageTitle)`
    margin-top: 10px;
  `,

  Image: styled.img`
    align-self: flex-end;
    height: 20px;
    width: 20px;
    margin: 0 10px 10px 0;
    cursor: pointer;
    z-index: 1000;
  `,
  ProductLogo: styled.img`
    height: 70px;
    @media ${screenSize.small} {
      max-width: 290px;
    }
  `,
  BlueLineForTextUnderBoxes: styled.div`
    ${blueUnderlineGradient}
    height: 30px;
    width: 200px;
    margin-left: 30px;
    margin-top: -15px;
  `,
  TextUnderBoxes: styled.div`
    margin: 30px auto;
    width: 270px;
    font-weight: bold;
    text-align: center;
  `,
  Top: styled.div`
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 10px;
  `,
  Note: styled.div`
    display: flex;
    margin-top: 25px;
    text-align: left;
    font-style: italic;
    max-width: 720px;
  `
}
